import { useVirtualizer } from "@tanstack/react-virtual";
import { useState, useRef, useEffect } from "react";

import { usePatchWorkspace } from "src/adminPanel/queries";
import { Button } from "src/base-components/Button";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedWs: WorkspaceDataplane;
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "undefined";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const HighlightText: React.FC<{ text: string; searchTerm: string }> = ({
  searchTerm,
  text,
}) => {
  if (!searchTerm) return <>{text}</>;

  const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <span key={i} className="bg-yellow-200">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </>
  );
};

const ConfigLabel: React.FC<{
  isChanged: boolean;
  searchTerm: string;
  text: string;
}> = ({ isChanged, searchTerm, text }) => (
  <Label>
    <div className="flex items-center gap-1">
      <HighlightText searchTerm={searchTerm} text={text} />
      {isChanged && (
        <span className="italic text-indigo-600 font-inter-normal-12px">
          Changed
        </span>
      )}
    </div>
  </Label>
);

export const ModifyWsConfigModal: React.FC<Props> = ({
  isOpen,
  onClose,
  selectedWs,
}) => {
  const [config, setConfig] = useState(selectedWs.config ?? {});
  const [searchTerm, setSearchTerm] = useState("");
  const parentRef = useRef<HTMLDivElement>(null);

  // Reset configs when modal opens with new workspace
  useEffect(() => {
    const newConfig = selectedWs.config ?? {};
    setConfig(newConfig);
  }, [selectedWs.id, selectedWs.config]);

  const patchWs = usePatchWorkspace();

  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredConfig = Object.entries(config).filter(([key]) => {
    return (
      key !== "aurora_generation_sufix" &&
      key !== "snapshot_ARN_aurora" &&
      key !== "use_shared_aurora_in_dev" &&
      key.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const rowVirtualizer = useVirtualizer({
    count: filteredConfig.length,
    estimateSize: () => 80, // Approximate height of each config row
    getScrollElement: () => parentRef.current,
    overscan: 5,
  });

  const handleConfigChange = (key: string, value: unknown) => {
    // Try to parse JSON if the value looks like a JSON string
    let parsedValue = value;
    if (
      typeof value === "string" &&
      (value.startsWith("{") || value.startsWith("["))
    ) {
      try {
        parsedValue = JSON.parse(value);
      } catch {
        // If parsing fails, keep the original string value
      }
    }

    setConfig((prev) => ({ ...prev, [key]: parsedValue }));
  };

  const getChanges = () => {
    const changes: Record<string, { from: unknown; to: unknown }> = {};

    Object.entries(config).forEach(([key, value]) => {
      const initialValue =
        selectedWs.config?.[key as keyof typeof selectedWs.config];

      if (JSON.stringify(value) !== JSON.stringify(initialValue)) {
        changes[key] = { from: initialValue, to: value };
      }
    });

    return changes;
  };

  return (
    <Modal open={isOpen} size="xl" onClose={onClose}>
      <Modal.Header>
        Modify Workspace <strong>{selectedWs.name}</strong> Configuration
      </Modal.Header>
      <Modal.Content>
        <div className="mb-4">
          <Input
            ref={searchInputRef}
            placeholder="Search config keys..."
            value={searchTerm}
            fullWidth
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "f") {
                e.preventDefault();
                e.currentTarget.focus();
              }
            }}
          />
        </div>
        <div ref={parentRef} className="h-[60vh] overflow-auto">
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: "relative",
              width: "100%",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const [key, value] = filteredConfig[virtualRow.index];
              const isChanged =
                JSON.stringify(value) !==
                JSON.stringify(
                  selectedWs.config?.[key as keyof typeof selectedWs.config],
                );

              return (
                <div
                  key={key}
                  ref={rowVirtualizer.measureElement}
                  className="mb-4"
                  data-index={virtualRow.index}
                  style={{
                    left: 0,
                    position: "absolute",
                    top: 0,
                    transform: `translateY(${virtualRow.start}px)`,
                    width: "100%",
                  }}
                >
                  <ConfigLabel
                    isChanged={isChanged}
                    searchTerm={searchTerm}
                    text={key}
                  />
                  <Input
                    value={
                      typeof value === "object"
                        ? JSON.stringify(value, null, 2)
                        : String(value)
                    }
                    fullWidth
                    monospaced
                    onChange={(event) => {
                      handleConfigChange(key, event.target.value);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer
        children={
          <div className="text-sm text-yellow-600">
            Warning: This will trigger a stack update, watch #tktl-releases
          </div>
        }
        primaryButton={
          <Button
            dataLoc="submit"
            loading={patchWs.isPending}
            variant="primary"
            onClick={async () => {
              const changes = getChanges();
              if (Object.keys(changes).length === 0) {
                window.alert("No changes detected.");
                return;
              }

              const changesList = Object.entries(changes)
                .map(
                  ([key, { from, to }]) =>
                    `- ${key}:\n  From: ${formatValue(from)}\n  To: ${formatValue(to)}`,
                )
                .join("\n\n");

              const confirmed = window.confirm(
                `Are you sure you want to update the workspace configuration?\n\n` +
                  `Changes:\n${changesList}`,
              );

              if (confirmed) {
                try {
                  const workspace = selectedWs;
                  workspace.config = config;
                  await patchWs.mutateAsync(workspace);
                  onClose();
                } catch (error) {
                  toastActions.failure({
                    title: "Failed to update workspace",
                    description:
                      error instanceof Error ? error.message : "Unknown error",
                  });
                }
              }
            }}
          >
            Submit
          </Button>
        }
      />
    </Modal>
  );
};
